import { Card } from "@mantine/core";
import { PauseRounded, PlayArrowRounded } from "@mui/icons-material";
import { File as FileEntity } from "@server/entities";
import { ActionIcon } from "@synqup/ui";
import React from "react";

import { getFilename } from "../../contexts/MediaLibrary/components/FileLibrary";
import { usePlayer, usePlayerState } from "../../contexts/Player";
import { MixdownTime } from "../../modules/projects/view/home/components/MixdownTime";
import { stemHeight } from "../../modules/stemviewer/helpers/constants";

import { WaveformLane } from "./waveform/WaveformLane";

interface PlayableAudioWaveformProps {
  file: FileEntity;
  isPlaying?: boolean;
}

export const PlayableAudioWaveform: React.FC<PlayableAudioWaveformProps> = ({
  file,
  isPlaying,
}) => {
  const { seek, playPause, load } = usePlayer();
  const {
    playing: _playing,
    currTime: _currTime,
    duration: _duration,
  } = usePlayerState(["playing", "currTime", "duration"]);

  const currTime = isPlaying ? _currTime : 0;
  const playing = isPlaying ? _playing : false;
  const duration = _duration || file.durationSeconds || 0;

  const handleSeek = (time: number) => {
    if (isPlaying) seek(time);
  };

  const handlePlayPause = () => {
    if (playing) return playPause();
    if (!file.url) return;
    if (isPlaying) return playPause();

    load({
      src: file.url,
      name: getFilename(file),
      artist: file.project?.artist || file.user.displayName,
      artwork: file.project?.albumArt,
      fileId: file.id,
    });
  };

  return (
    <>
      <Card
        className="mt-6 p-1 shadow-2xl touch-none"
        style={{ background: "linear-gradient(90deg, #0D0A1F, #1A050C)" }}
        data-testid="project-mixdown"
        data-quick-assist-id="project-mixdown"
      >
        <WaveformLane
          file={file}
          currentTime={currTime}
          playing={playing}
          duration={duration}
          onSeek={handleSeek}
        />
      </Card>

      <Card className="relative flex items-center justify-center mb-24 md:mb-12 rounded-tr-none rounded-tl-none p-2">
        <div className="w-full absolute flex items-center gap-2 left-2">
          <MixdownTime />
        </div>

        <ActionIcon
          data-testid="audio-waveform-play-pause"
          variant={playing ? "light" : "filled"}
          color={playing ? "gray" : "primary"}
          onClick={handlePlayPause}
        >
          {playing ? <PauseRounded /> : <PlayArrowRounded />}
        </ActionIcon>
      </Card>
    </>
  );
};

export const draw = (
  canvas: HTMLCanvasElement,
  arrayBuffer: number[],
  height = stemHeight
) => {
  const cvs = canvas;
  if (!cvs) return;

  const ctx = cvs.getContext("2d");
  if (!ctx) return;

  // set width of canvas element
  const scale = window.devicePixelRatio; // Change to 1 on retina screens to see blurry canvas.
  const width = arrayBuffer.length * 4;

  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.width = Math.floor(width * scale);
  canvas.height = Math.floor(height * scale);

  ctx.fillStyle = "white";
  ctx.strokeStyle = "white";

  const dpi = window.devicePixelRatio;
  ctx.scale(dpi, dpi);

  const max = Math.max(...arrayBuffer);

  for (let i = 1; i < arrayBuffer.length; i += 1) {
    const a = (arrayBuffer[i] * height) / (max * 2) - 0.5;
    const x2 = i * 4;
    const y2 = height / 2 - a;
    const x3 = i * 4;
    const y3 = height / 2 + a;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.stroke();
  }
};
